import { useState, useEffect } from 'react';
import { Card, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useRole } from '@/hooks/useRole';
import styles from './Legal.module.css';

interface LegalPage {
  id: string;
  title: string;
  content: string;
  updated_at: string;
}

function mdToHtml(md: string): string {
  return md
    .replace(/^\|(.+)\|$/gm, (_, row) => {
      const cells = row.split('|').map((c: string) => c.trim());
      if (/^-+$/.test(cells[0])) return '';
      const tag = cells.every((c: string) => /^\*\*.*\*\*$/.test(c)) ? 'th' : 'td';
      return '<tr>' + cells.map((c: string) => `<${tag}>${c.replace(/\*\*/g, '')}</${tag}>`).join('') + '</tr>';
    })
    .replace(/(<tr>.*<\/tr>\n?)+/g, (block) => `<table>${block}</table>`)
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, (block) => `<ol>${block}</ol>`)
    .replace(/^---$/gm, '<hr />')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^(?!<[a-z])((?!^\s*$).+)$/gm, '<p>$1</p>')
    .replace(/<p><\/p>/g, '')
    .replace(/\n{2,}/g, '\n');
}

const TABS: { id: string; label: string }[] = [
  { id: 'terms', label: '利用規約' },
  { id: 'privacy', label: 'プライバシーポリシー' },
  { id: 'tokushoho', label: '特定商取引法' },
];

export function Legal() {
  const { isAdmin } = useRole();
  const { data: pages, refetch } = useApi<LegalPage[]>('/console/legal');
  const [activeTab, setActiveTab] = useState('terms');
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const current = pages?.find(p => p.id === activeTab);

  useEffect(() => {
    if (current) setDraft(current.content);
  }, [current]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api(`/console/legal/${activeTab}`, {
        method: 'PUT',
        body: JSON.stringify({ content: draft }),
      });
      toast('保存しました');
      setMode('preview');
      refetch();
    } catch {
      toast('保存に失敗しました', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.tabs}>
        {TABS.map(t => (
          <button
            key={t.id}
            className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
            onClick={() => { setActiveTab(t.id); setMode('preview'); }}
          >{t.label}</button>
        ))}
      </div>

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.modeToggle}>
            <button
              className={`${styles.modeBtn} ${mode === 'preview' ? styles.modeBtnActive : ''}`}
              onClick={() => setMode('preview')}
            >プレビュー</button>
            {isAdmin && (
              <button
                className={`${styles.modeBtn} ${mode === 'edit' ? styles.modeBtnActive : ''}`}
                onClick={() => setMode('edit')}
              >編集</button>
            )}
          </div>
          {current && (
            <span className={styles.updatedAt}>
              最終更新: {new Date(current.updated_at).toLocaleDateString('ja-JP')}
            </span>
          )}
          {mode === 'edit' && (
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? '保存中...' : '保存'}
            </button>
          )}
        </div>

        {mode === 'edit' ? (
          <textarea
            className={styles.editor}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
          />
        ) : (
          <div
            className={styles.preview}
            dangerouslySetInnerHTML={{ __html: current ? mdToHtml(current.content) : '' }}
          />
        )}
      </Card>
    </div>
  );
}
